import { useState } from 'react'
import type { DragEvent, ChangeEvent } from 'react'
import type { FullPokemonCard } from '@/types/pokemon'
import { CardDetails } from './CardDetails'
import { CardmarketLivePrices } from './CardmarketLivePrices'
import { addCard } from '../services/inventoryService'
import ScanCardAlternatives from './ScanCardAlternatives'
import type { StandardCondition } from '@/types/inventory'
import { AddToInventoryModal } from './inventory/AddToInventoryModal'
import { analyzeCard as analyzeCardService } from '@/services/cardService'

export function ScanCardTab() {
  const [isDragging, setIsDragging] = useState(false)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [cardData, setCardData] = useState<FullPokemonCard | null>(null)
  const [possibleCards, setPossibleCards] = useState<FullPokemonCard[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [addingToInventory, setAddingToInventory] = useState(false)
  const [addSuccess, setAddSuccess] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = (error) => reject(error)
      reader.readAsDataURL(file)
    })
  }

  const analyzeCard = async (imageFile: File) => {
    setLoading(true)
    setError(null)
    setAddSuccess(false) // Reset success message
    try {
      const result = await analyzeCardService(imageFile)

      setCardData(result.bestMatch ?? null)
      setPossibleCards(result.possibleCards ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleAddToInventoryClick = () => {
    if (!cardData) return
    setShowAddModal(true)
  }

  const handleModalConfirm = async (condition: StandardCondition, priceWhenAdded: number | null) => {
    if (!cardData) return

    setAddingToInventory(true)
    setAddSuccess(false)
    setShowAddModal(false)

    try {
      await addCard({
        cardId: cardData.id,
        condition,
        priceWhenAdded: priceWhenAdded ?? undefined,
      })

      setAddSuccess(true)
      setTimeout(() => setAddSuccess(false), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add card to inventory')
    } finally {
      setAddingToInventory(false)
    }
  }

  const handleModalClose = () => {
    setShowAddModal(false)
  }

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file')
      return
    }

    try {
      const base64 = await convertToBase64(file)
      setImagePreview(base64)
      await analyzeCard(file)
    } catch {
      setError('Failed to process image')
    }
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)

    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      handleFile(files[0])
    }
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      handleFile(files[0])
    }
  }

  const handleSelectAlternativeCard = async (card: FullPokemonCard) => {
    setCardData(card)
    setAddSuccess(false)
  }

  const resetCard = () => {
    setImagePreview(null)
    setCardData(null)
    setPossibleCards([])
    setError(null)
    setAddSuccess(false)
  }

  return (
    <div className="grid lg:grid-cols-[450px_1fr] grid-cols-1 gap-8 items-start">
      <div className="w-full">
        <div
          className={`border-3 border-dashed rounded-2xl p-12 text-center transition-all duration-300 min-h-[400px] flex items-center justify-center ${
            isDragging ? 'border-blue-600 bg-blue-50 scale-[1.02]' : 'border-blue-300 bg-blue-50/30'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {imagePreview ? (
            <div className="flex flex-col gap-6 w-full">
              <img src={imagePreview} alt="Card preview" className="max-w-full max-h-[500px] object-contain rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]" />
              <button
                className="py-3 px-8 bg-blue-50 text-blue-700 border-2 border-blue-300 rounded-lg cursor-pointer font-semibold text-base transition-all duration-300 hover:bg-blue-100 hover:border-blue-400"
                onClick={resetCard}
              >
                Upload Another Card
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-blue-600 opacity-60"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <p className="text-xl font-medium text-gray-700 m-0">Drag & drop a Pokemon card image here</p>
              <p className="text-base text-gray-500 m-0">or</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <label className="inline-flex items-center gap-2 py-3 px-8 bg-blue-800 text-white rounded-lg cursor-pointer font-semibold transition-all duration-300 text-base hover:bg-blue-900">
                  <input type="file" accept="image/*" onChange={handleFileInput} className="hidden" />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  Choose File
                </label>
                <label className="inline-flex items-center gap-2 py-3 px-8 bg-gray-700 text-white rounded-lg cursor-pointer font-semibold transition-all duration-300 text-base hover:bg-gray-800">
                  <input type="file" accept="image/*" capture="environment" onChange={handleFileInput} className="hidden" />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  Take Photo
                </label>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl p-8 min-h-[400px] border border-gray-200 w-full overflow-x-auto shadow-sm">
        <h2 className="mt-0 mb-6 text-3xl text-gray-900">Card Information</h2>

        {loading && (
          <div className="flex flex-col items-center gap-4 p-8">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            <p className="text-gray-500 m-0">Analyzing card...</p>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 mb-6">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="shrink-0"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {error}
          </div>
        )}

        {cardData && !loading && (
          <>
            <CardmarketLivePrices card={cardData} />

            {/* Add to Inventory — available even without Cardmarket price */}
            <button
              className="w-full py-4 px-8 bg-emerald-600 text-white border-none rounded-xl cursor-pointer font-semibold text-lg transition-all duration-300 mt-6 mb-6 shadow-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleAddToInventoryClick}
              disabled={addingToInventory}
            >
              ➕ Add to Inventory
            </button>

            {addSuccess && (
              <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 mb-6">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="shrink-0"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                Card added to inventory successfully!
              </div>
            )}

            <ScanCardAlternatives possibleCards={possibleCards} selectedCard={cardData} selectedCardId={cardData.id} onSelectCard={handleSelectAlternativeCard} />

            <CardDetails card={cardData} />
          </>
        )}

        {!cardData && !loading && !error && (
          <div className="p-8 text-center">
            <p className="text-gray-600 text-base m-0">Upload a Pokemon card image to see its details</p>
          </div>
        )}
      </div>

      {/* Add to Inventory Modal */}
      <AddToInventoryModal
        key={showAddModal ? (cardData?.id ?? 'no-card') : 'closed'}
        isOpen={showAddModal}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
        card={cardData}
      />
    </div>
  )
}
